import { useEffect, useState } from 'react'
import { Channel as StreamChannel, StreamChat } from 'stream-chat'
import api from '../../../services/api.services'
import retry from 'async-retry'
import { currentLang, currentLocale } from '../../../i18nInit'
import { GoalId, Lang, UserId } from '../../../services/api.types'
import { App } from '@capacitor/app'
import { Keyboard } from '@capacitor/keyboard'
import { PluginListenerHandle } from '@capacitor/core'
import { isIOS, isNative } from '../../../common/helpers/isNative'
import { ROUTES } from '../../../common/Routes/routePaths'
import { push } from 'connected-react-router'
import { store } from '../../../index'

import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { LocalNotifications } from '@capacitor/local-notifications'
import Bugsnag from '@bugsnag/js'
import { isDev, isStaging } from '../../../services/apiUrl'
import { getUserCompanies } from '../../RegisterUser/store/RegisterUserSelectors'
import { IGlobalState } from '../../../Root/store/appReducer'
import { hasFeatureFlag } from '../../../common/helpers/featureFlags'

const apiKey = isStaging || isDev ? 'k2bqeduycumb' : 'xspnfq6bryme'
export const channelType = 'challenge'

const fetchChatTokenFromApi = async () => {
  // @ts-ignore
  const { userToken }: { userToken: string } = await api.activity.getChatUserToken()
  return userToken
}
const retryableChatTokenProvider = () => retry(fetchChatTokenFromApi)

const requestPushPermission = async () => {
  let permStatus = await FirebaseMessaging.checkPermissions()

  if (permStatus.receive === 'prompt') {
    permStatus = await FirebaseMessaging.requestPermissions()
  }

  if (permStatus.receive !== 'granted') {
    throw new Error('User denied permissions!')
  }
}

/*export const registerPushTokenForChat = async (chatClient: StreamChat, userId: UserId, token: string) => {
  // console.log('New token: ', token)

  try {
    const { value: oldPushToken } = await Preferences.get({ key: 'currentPushToken' })
    // console.log('Old token: ', token)

    // TODO: This is invalid, because we have probably already stored the existing token.
    //if (oldPushToken == null || oldPushToken !== token) {
    // console.log('Token has changed')
    chatClient.addDevice(token, 'firebase').then(async () => {
      // console.log('Add device success')
      await Preferences.set({ key: 'currentPushToken', value: token })
      if (oldPushToken !== null) {
        await chatClient.removeDevice(oldPushToken)
        // console.log('Removed old token')
      }
    })

    // Also remove old push token from our database.
    // TODO: Move to somewhere else?
    if (oldPushToken !== null) {
      await api.users.removeUserDevice({ token: oldPushToken })
    }
    //}
  } catch (e: any) {
    console.error('Chat pushNotification addDevice error: ', e)
  }
}*/

/*export const requestAndRegisterPushToken = async (chatClient: StreamChat, userId: UserId) => {
  try {
    // This will throw an error if user denies permission.
    await requestPushPermission()
    const result = await FirebaseMessaging.getToken()
    if (chatClient && result.token) await registerPushTokenForChat(chatClient, userId, result.token)
  } catch (e: any) {
    console.log('requestAndRegisterPushToken failed | error: ', e)
  }
}*/

const clearChannelPushNotifications = async (goalId: GoalId, name?: string) => {
  if (isNative()) {
    if (isIOS) {
      const notificationList = await FirebaseMessaging.getDeliveredNotifications()
      if (notificationList?.notifications?.length > 0) {
        // console.log('iOS notifications', JSON.stringify(notificationList, null, 2))
        const notificationsToDelete = {
          notifications: notificationList.notifications.filter(
            ({ data }: any) => data?.channel_id === goalId || data?.cap_extra?.channel_id === goalId,
          ),
          //.map(({ id }: any) => id),
        }
        if (notificationsToDelete?.notifications?.length > 0) {
          // console.log('[iOS] deleting notifications', JSON.stringify(notificationsToDelete, null, 2))
          await FirebaseMessaging.removeDeliveredNotifications(notificationsToDelete)
        }
      }
    } else {
      const notificationList = await LocalNotifications.getDeliveredNotifications()
      if (notificationList?.notifications?.length > 0) {
        // console.log('ANDROID notifications', JSON.stringify(notificationList, null, 2))
        if (!name) {
          // Android notifications do not have groups when sending from GetStream.
          await FirebaseMessaging.removeAllDeliveredNotifications()
        } else {
          const channelSuffix = ` (${name})`
          const notificationsToDelete = {
            notifications: notificationList.notifications.filter(({ title }: any) => title?.endsWith(channelSuffix)),
            //.map(({ id }: any) => id),
          }
          if (notificationsToDelete?.notifications?.length > 0) {
            // console.log('[android] deleting notifications', JSON.stringify(notificationsToDelete, null, 2))
            await LocalNotifications.removeDeliveredNotifications(notificationsToDelete)
          }
        }
      }
    }
  }
}

let chatClient: StreamChat | undefined
export const getChatClient = async (userId: UserId, name: string) => {
  if (chatClient && chatClient.userID) {
    return chatClient
  }
  chatClient = StreamChat.getInstance(apiKey)

  try {
    /*const state = (await store.getState()) as any as IGlobalState
    const user = await getUserDto(state)
    if (!user.userId) {
      console.error('getChatClient | Could not get user data!')
      return undefined
    } else {
    const { userId, firstName, lastName, email } = user
    const name = getFullName(firstName, lastName, email)
    console.log(`getChatClient | userId: ${userId} | name: ${name}`)*/

    // Open the WebSocket connection to start receiving events.
    // console.log('Chat connectUser')
    await chatClient.connectUser(
      {
        id: userId,
        name,
        // @ts-ignore
        language:
          currentLang === Lang.EN
            ? 'en'
            : currentLang === Lang.ZH_HANS
              ? 'zh'
              : currentLang === Lang.ZH_HANT
                ? 'zh-TW'
                : currentLang === Lang.NO
                  ? 'no'
                  : currentLocale,
        //image: 'https://getstream.io/random_png/?id=square-glade-6&name=square',
      },
      retryableChatTokenProvider,
    )
    return chatClient
    //}
  } catch (e: any) {
    console.error('Chat connectUser error: ', e)
    return undefined
  }
}

export const useInitChat = (userId: UserId, name: string, goalId: GoalId, participating?: boolean) => {
  const [chatClient, setChatClient] = useState<StreamChat>()
  const [currentChannel, setCurrentChannel] = useState<StreamChannel>()

  const [appIsActive, setAppIsActive] = useState(true)
  useEffect(() => {
    let listenerHandle: PluginListenerHandle | undefined
    const setup = async () => {
      listenerHandle = await App.addListener('appStateChange', ({ isActive }) => {
        setAppIsActive(isActive)
      })
    }
    setup()
    return () => {
      listenerHandle?.remove()
    }
  }, [])
  useEffect(() => {
    console.log('useInitChat | state: ', appIsActive)
    if (appIsActive) {
      // @ts-ignore // name exists!
      const channelName = currentChannel?.data?.name
      // console.log('app resume', channelName)
      if (channelName) clearChannelPushNotifications(goalId, channelName)
    }
  }, [appIsActive])

  useEffect(() => {
    chatClient?.setMessageComposerSetupFunction(({ composer }) => {
      const userCompanies = getUserCompanies(store.getState() as any as IGlobalState)
      const allowAllFiles = hasFeatureFlag(userCompanies, 'chatAllUpload')
      const allowVideoFiles = hasFeatureFlag(userCompanies, 'chatVideoUpload')
      composer.updateConfig({
        attachments: {
          acceptedFiles: allowAllFiles ? [] : allowVideoFiles ? ['image/*', 'video/*'] : ['image/*'],
        },
        location: {
          enabled: false,
        },
      })
    })
  }, [chatClient /*, emojisEnabled*/])

  /*const onBackground = useCallback(() => {
    if (!chatClient) return

    chatClient.closeConnection()
  }, [chatClient])

  const onForeground = useCallback(() => {
    if (!chatClient) return

    chatClient.openConnection()

    // Pulls in new messages.
    chatClient.recoverState()
  }, [chatClient])

  useEffect(() => (state ? onForeground() : onBackground()), [state])*/

  const switchChannel = async (channelId: string) => {
    console.log('Chat switchChannel', channelId)
    if (!chatClient) {
      return setCurrentChannel(undefined)
    }

    try {
      const newChannel = chatClient.channel(channelType, channelId)
      await newChannel.watch({ messages: { limit: 10 } })

      //setUnreadStatus(channelId, false);
      setCurrentChannel(newChannel)
    } catch (e: any) {
      console.error('Chat switchChannel error: ', e)
    }
  }

  useEffect(() => {
    // console.log('goalId useEffect')
    let keyboardDidShowListener: PluginListenerHandle
    //let pushNotificationsRegisterListener: PluginListenerHandle

    const initChat = async () => {
      console.log('initChat')
      try {
        await api.activity.joinChat({ goalId })

        const client = await getChatClient(userId, name)
        if (!client) throw new Error('getChatClient failed')

        const channel = client.channel(channelType, goalId)
        await channel.watch({ messages: { limit: 10 } })

        /*client.on('message.new', handleDmMessages);
        client.on('notification.message_new', handleDmMessages);*/

        if (isNative()) {
          keyboardDidShowListener = await Keyboard.addListener('keyboardDidShow', (info) => {
            const chatList = document.getElementsByClassName('str-chat__list')
            if (chatList[0]) chatList[0].scrollTop = chatList[0].scrollHeight

            const chatModal = document.getElementsByClassName('str-chat__modal--open') as HTMLCollectionOf<HTMLElement>
            if (chatModal[0]) chatModal[0].style.paddingBottom = info.keyboardHeight - 81 + 'px'
          })
        }

        setChatClient(client)
        setCurrentChannel(channel)

        //clearChannelPushNotifications(goalId, channel)

        /*if (isNative()) {
          console.log('PushNotifications.register() | chat view')
          await requestAndRegisterPushToken(client, userId)
        }*/
      } catch (e: any) {
        console.log('useChatInit redirection | error: ', e)
        Bugsnag.notify(e)
        await store.dispatch(push(ROUTES.CLIENT_ACTIVITY.to))
      }

      return () => {
        if (keyboardDidShowListener) keyboardDidShowListener.remove()
        //if (pushNotificationsRegisterListener) pushNotificationsRegisterListener.remove()
      }
    }

    if (!chatClient) {
      initChat()
    } else {
      switchChannel(goalId)
    }
  }, [goalId])

  useEffect(() => {
    console.log('currentChannel useEffect')
    // @ts-ignore // name exists!
    const channelName = currentChannel?.data?.name
    if (channelName) clearChannelPushNotifications(goalId, channelName)
  }, [currentChannel])

  return { chatClient, currentChannel }
}
